import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CheckCircle,
  AlertTriangle,
  Loader2,
  RefreshCw,
  Lock,
  HelpCircle,
  Building2,
  Wallet
} from 'lucide-react';
import Button from '../../components/ui/Button';
import PageHeader from '../../components/ui/PageHeader';
import { useApp, PAKISTAN_BANKS } from '../../context/AppContext';
import { useBroker } from '../../context/BrokerContext';
import { backendOTPService, documentNotificationService } from '../../services/emailService';
import { whatsappAPI } from '../../services/api';
import './OTPVerification.css';

function OTPVerification() {
  const navigate = useNavigate();
  const {
    currentTransaction,
    setCurrentTransaction,
    addPendingRegistration,
    userType
  } = useApp();
  const { getBrokerPath } = useBroker();

  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [verificationState, setVerificationState] = useState('idle'); // idle, sending, sent, verifying, success, error
  const [resendTimer, setResendTimer] = useState(0);

  const inputRefs = useRef([]);
  const otpSentRef = useRef(false); // Prevent double OTP send in React Strict Mode

  // Get bank logo
  const getBankLogo = (bankCode) => {
    const bank = PAKISTAN_BANKS.find(b => b.code === bankCode);
    const isWallet = bank?.type === 'wallet';
    const logoUrl = bank?.logo;

    return (
      <div className="bank-logo-wrapper">
        {logoUrl ? (
          <img
            src={logoUrl}
            alt={bank?.name || bankCode}
            className="bank-logo"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
        ) : null}
        <div className="bank-logo-fallback" style={{ display: logoUrl ? 'none' : 'flex' }}>
          {isWallet ? <Wallet size={18} /> : <Building2 size={18} />}
        </div>
      </div>
    );
  };

  // Check if this is a returning user flow
  const isReturningUser = currentTransaction?.isReturningUser || userType === 'returning';

  // Redirect if no transaction (but allow returning users, IBAN verified, or receipt uploaded)
  useEffect(() => {
    if (!currentTransaction) {
      navigate(getBrokerPath('/'));
    } else if (!isReturningUser && !currentTransaction.ibanVerified && !currentTransaction.receiptUploaded) {
      navigate(getBrokerPath('/'));
    }
  }, [currentTransaction, navigate, isReturningUser, getBrokerPath]);

  // Auto-send OTP on mount (with protection against React Strict Mode double-call)
  useEffect(() => {
    if (currentTransaction && verificationState === 'idle' && !otpSentRef.current) {
      // For returning users, new users with verified IBAN, or users who uploaded receipt
      if (isReturningUser || currentTransaction.ibanVerified || currentTransaction.receiptUploaded) {
        otpSentRef.current = true; // Mark as sent to prevent duplicate calls
        handleSendOTP();
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Resend timer countdown
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  const handleSendOTP = async () => {
    setVerificationState('sending');
    setError('');

    try {
      // Debug: Log the data being sent
      console.log('[OTP] Sending OTP with data:', {
        email: currentTransaction.userEmail,
        phone: currentTransaction.userPhone,
        userName: currentTransaction.userName
      });

      // Use backend service - OTP is generated securely on server
      const result = await backendOTPService.sendOTP(
        currentTransaction.userEmail,
        currentTransaction.userPhone,
        currentTransaction.userName
      );

      console.log('[OTP] Backend response:', result);

      if (result.success) {
        setVerificationState('sent');
        setResendTimer(60); // 60 seconds before resend
      } else {
        throw new Error(result.error || 'Failed to send OTP');
      }
    } catch (err) {
      console.error('[OTP] Send error:', err);
      console.error('[OTP] Full error details:', {
        message: err.message,
        stack: err.stack
      });
      setError('Failed to send OTP. Please try again.');
      setVerificationState('idle');
    }
  };

  const handleOtpChange = (index, value) => {
    // Only allow numbers
    if (value && !/^\d$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    setError('');

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-verify when all digits entered
    if (newOtp.every(digit => digit) && newOtp.join('').length === 6) {
      handleVerifyOTP(newOtp.join(''));
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 6);
    if (/^\d+$/.test(pastedData)) {
      const newOtp = pastedData.split('').concat(Array(6).fill('')).slice(0, 6);
      setOtp(newOtp);
      if (pastedData.length === 6) {
        handleVerifyOTP(pastedData);
      }
    }
  };

  const handleVerifyOTP = async (enteredOtp) => {
    setVerificationState('verifying');
    setError('');

    try {
      // Verify OTP on backend (secure validation)
      const verification = await backendOTPService.verifyOTP(
        currentTransaction.userEmail,
        currentTransaction.userPhone,
        enteredOtp
      );

      if (verification.valid) {
        setVerificationState('success');

        // Update transaction with OTP verified status
        const updatedTransaction = {
          ...currentTransaction,
          otpVerified: true,
          verifiedAt: new Date().toISOString(),
        };

        // ALWAYS add pending registration for any bank account submission
        // This ensures admin approval is required for all new transactions
        console.log('[OTP] Full currentTransaction object:', JSON.stringify(currentTransaction, null, 2));

        const hasBankDetails = !!(currentTransaction.bankName || currentTransaction.iban || currentTransaction.bankAccountNumber);
        console.log('[OTP] Has bank details:', hasBankDetails, {
          bankName: currentTransaction.bankName,
          iban: currentTransaction.iban,
          bankAccountNumber: currentTransaction.bankAccountNumber
        });

        // Always add to pending - force add for now
        updatedTransaction.accountStatus = 'pending';
        updatedTransaction.submittedAt = new Date().toISOString();

        // Create registration object
        const registrationData = {
          id: `PR-${Date.now()}`,
          userId: currentTransaction.userId,
          cnic: currentTransaction.userCnic,
          userName: currentTransaction.userName,
          phone: currentTransaction.userPhone,
          userPhone: currentTransaction.userPhone,
          email: currentTransaction.userEmail,
          userEmail: currentTransaction.userEmail,
          bankCode: currentTransaction.bankCode,
          bankName: currentTransaction.bankName,
          iban: currentTransaction.iban,
          accountNumber: currentTransaction.bankAccountNumber,
          accountTitle: currentTransaction.bankAccountTitle,
          status: 'pending',
          submittedAt: new Date().toISOString(),
          ocrVerified: currentTransaction.ocrVerified || false,
          requiresManualReview: currentTransaction.requiresManualReview || false,
          // Include document for email attachment
          documentBase64: currentTransaction.ibanDocumentBase64,
          documentName: currentTransaction.ibanDocumentName,
          documentType: currentTransaction.ibanDocumentType,
        };

        console.log('[OTP] Registration data to be saved:', JSON.stringify(registrationData, null, 2));

        // Add to pending registrations (not whitelisted yet)
        try {
          await addPendingRegistration(registrationData);
          console.log('[OTP] Successfully called addPendingRegistration');
        } catch (regError) {
          console.error('[OTP] Error calling addPendingRegistration:', regError);
        }

        console.log('[OTP] Added pending registration for new bank account');

        // Send notification via WhatsApp that account is pending
        whatsappAPI.sendAccountWhitelisted(
          currentTransaction.userPhone,
          currentTransaction.bankAccountTitle,
          currentTransaction.bankName
        );

        setCurrentTransaction(updatedTransaction);

        // Send receipt/bank proof notification email after successful OTP verification
        console.log('[OTP Success] Checking email notifications:', {
          userEmail: currentTransaction.userEmail,
          receiptUploaded: currentTransaction.receiptUploaded,
          ocrData: !!currentTransaction.ocrData,
          ibanVerified: currentTransaction.ibanVerified,
          isReturningUser: isReturningUser
        });

        if (currentTransaction.userEmail) {
          // Check if this is a receipt upload flow (has receipt data)
          if (currentTransaction.receiptUploaded && currentTransaction.ocrData) {
            const status = currentTransaction.verificationStatus === 'verified' ? 'verified'
              : currentTransaction.verificationStatus === 'mismatch' ? 'mismatch'
              : 'manual_review';

            documentNotificationService.sendReceiptNotification({
              email: currentTransaction.userEmail,
              userName: currentTransaction.userName || currentTransaction.bankAccountTitle,
              transactionId: currentTransaction.id,
              referenceNumber: currentTransaction.referenceNumber,
              amount: currentTransaction.amount,
              bankName: currentTransaction.bankName,
              accountTitle: currentTransaction.bankAccountTitle,
              status: status,
              ocrResult: currentTransaction.ocrData,
              mismatchDetails: currentTransaction.mismatchDetails,
              // Include the uploaded receipt as attachment
              documentBase64: currentTransaction.receiptBase64,
              documentName: currentTransaction.receiptName,
              documentType: currentTransaction.receiptType,
            }).then(result => {
              console.log('[OTP Success] Receipt notification sent:', result);
            }).catch(err => {
              console.error('[OTP Success] Receipt notification error:', err);
            });
          }
          // Check if this is a bank proof/IBAN verification flow (new user adding bank account)
          // Send for any new user with IBAN verified OR any user who has bank details but no receipt
          else if (!isReturningUser && (currentTransaction.ibanVerified || currentTransaction.bankName)) {
            console.log('[OTP Success] Sending bank proof notification...');
            documentNotificationService.sendBankProofNotification({
              email: currentTransaction.userEmail,
              userName: currentTransaction.userName,
              bankName: currentTransaction.bankName,
              iban: currentTransaction.iban,
              accountNumber: currentTransaction.bankAccountNumber,
              accountTitle: currentTransaction.bankAccountTitle,
              submissionId: `PR-${Date.now()}`,
              status: 'pending',
              // Include the uploaded document as attachment
              documentBase64: currentTransaction.ibanDocumentBase64,
              documentName: currentTransaction.ibanDocumentName,
              documentType: currentTransaction.ibanDocumentType,
            }).then(result => {
              console.log('[OTP Success] Bank proof notification sent:', result);
            }).catch(err => {
              console.error('[OTP Success] Bank proof notification error:', err);
            });
          }
        }

        // Navigate based on user type and flow
        setTimeout(() => {
          if (currentTransaction.receiptUploaded) {
            // Receipt uploaded - go to transaction status page
            navigate(getBrokerPath(`/status/${currentTransaction.id}`));
          } else if (isReturningUser) {
            // Returning user with approved account - go to payment details
            navigate(getBrokerPath('/payment-details'));
          } else {
            // New user - go back to dashboard to see pending status
            navigate(getBrokerPath('/account-dashboard'));
          }
        }, 2500);
      } else {
        setVerificationState('sent');
        setError(verification.reason || 'Invalid OTP');
        setOtp(['', '', '', '', '', '']);
        inputRefs.current[0]?.focus();
      }
    } catch (err) {
      console.error('[OTP] Verify error:', err);
      setVerificationState('sent');
      setError('Verification failed. Please try again.');
      setOtp(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    }
  };

  if (!currentTransaction) {
    return null;
  }

  // For new users, require IBAN verification or receipt upload; for returning users, skip this check
  if (!isReturningUser && !currentTransaction.ibanVerified && !currentTransaction.receiptUploaded) {
    return null;
  }

  return (
    <div className="otp-container animate-fade-in">
      <div className="otp-main-card">
        {/* Header Section */}
        <PageHeader showBackButton={false} greeting="Verify your identity" />

        {/* Content Section */}
        <div className="otp-content">
          {verificationState === 'sending' && (
            <div className="sending-state">
              <Loader2 className="spinner" size={48} />
              <p>Sending OTP to your WhatsApp and Email...</p>
            </div>
          )}

          {(verificationState === 'sent' || verificationState === 'verifying' || verificationState === 'idle') && verificationState !== 'sending' && (
            <>
              {/* OTP sent message - simplified for security */}
              <div className="otp-sent-banner">
                <CheckCircle size={20} className="success-icon" />
                <p>OTP has been sent to your registered contact</p>
              </div>

              {/* OTP Input Fields */}
              <div className="section-header">
                <Lock size={16} />
                <span>Enter 6-digit Code</span>
              </div>

              <div className="otp-input-container">
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    ref={el => inputRefs.current[index] = el}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    onPaste={handlePaste}
                    className={`otp-input ${error ? 'error' : ''} ${digit ? 'filled' : ''}`}
                    disabled={verificationState === 'verifying'}
                  />
                ))}
              </div>

              {error && (
                <div className="error-message">
                  <AlertTriangle size={18} />
                  <span>{error}</span>
                </div>
              )}

              {verificationState === 'verifying' && (
                <div className="verifying-indicator">
                  <Loader2 className="spinner" size={20} />
                  <span>Verifying...</span>
                </div>
              )}

              {/* Resend OTP */}
              <div className="resend-section">
                {resendTimer > 0 ? (
                  <p className="resend-timer">Resend code in {resendTimer}s</p>
                ) : (
                  <Button
                    variant="ghost"
                    icon={RefreshCw}
                    onClick={handleSendOTP}
                    disabled={verificationState === 'verifying'}
                  >
                    Resend Code
                  </Button>
                )}
              </div>

              {/* Delivery Status Info */}
              <div className="switch-method">
                <p>OTP is sent via both WhatsApp and Email for security</p>
              </div>
            </>
          )}

          {verificationState === 'success' && (
            <div className="success-state">
              <div className="success-icon">
                <CheckCircle size={64} />
              </div>
              <h3>{isReturningUser ? 'Identity Verified!' : 'Account Submitted!'}</h3>
              <p>
                {isReturningUser
                  ? 'Your identity has been verified. Proceeding to payment.'
                  : 'Your bank account has been submitted for approval.'}
              </p>
              {!isReturningUser && (
                <>
                  <div className="success-details">
                    <div className="detail-item">
                      <span className="label">Account</span>
                      <span className="value">{currentTransaction.bankAccountTitle}</span>
                    </div>
                    <div className="detail-item bank-detail">
                      <span className="label">Bank</span>
                      <span className="value with-logo">
                        {getBankLogo(currentTransaction.bankCode)}
                        {currentTransaction.bankName}
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Status</span>
                      <span className="value pending-status">Pending Approval</span>
                    </div>
                  </div>
                  <p className="pending-note">
                    You will be notified via WhatsApp/Email once your account is approved.
                  </p>
                </>
              )}
              <p className="redirect-message">
                {isReturningUser ? 'Proceeding to payment...' : 'Returning to dashboard...'}
              </p>
            </div>
          )}
        </div>

        {/* Footer Section */}
        <div className="otp-footer">
          <div className="footer-title">
            <HelpCircle size={14} />
            <span>Need Help?</span>
          </div>
          <div className="help-tips">
            <p>Check your spam/junk folder for emails</p>
            <p>OTP expires in 5 minutes</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default OTPVerification;
